import * as React from "react";
import { ImageLoader } from "../ImageLoader";
import { cn } from "~/lib/utils";
import { ClassnamePassthruWrapper } from "~/components/ui/classname-passthru-wrapper";

// // // //

interface Props {
    id?: string;
    title: string | React.ReactNode;
    subtitle?: string | React.ReactNode | null;
    icon?: string | React.ReactNode;
    badge?: React.ReactNode;
    controls?: React.ReactNode;
}

/**
 * PageHeader
 * Renders a page header component with a title and subtitle
@param {string} props.title - The title of the page header
@param {string} props.subtitle - The subtitle of the page header.
@param {string} props.icon - The icon to display next to the header
@param {string} props.badge - The badge to display next to the header
@param {React.ReactNode} props.controls - Right-justified controls (e.g. buttons) rendered alongside the title
*/
export function PageHeader(props: Props) {
    const {
        id,
        title,
        icon,
        subtitle = null,
        badge = null,
        controls = null,
    } = props;

    return (
        <div
            id={id}
            className={cn(
                "grid grid-cols-1 gap-x-4 md:mt-2 select-none md:grid-cols-[1fr_auto]",
                {
                    "mb-2": subtitle === null,
                },
            )}
        >
            <ClassnamePassthruWrapper
                className={cn("md:col-start-1 md:row-start-1", {
                    "flex items-center gap-3 mt-1": badge !== null,
                })}
            >
                <h1
                    className={cn(
                        "font-sans font-light text-[2.438rem]! leading-normal text-foreground flex items-center gap-3",
                        {
                            "mt-1": badge === null,
                        },
                    )}
                >
                    {typeof icon === "string" && (
                        <ImageLoader className="w-10" src={icon} alt="" />
                    )}
                    {icon !== null && typeof icon !== "string" && <>{icon}</>}
                    {title}
                </h1>
                {badge !== null && <>{badge}</>}
            </ClassnamePassthruWrapper>

            {subtitle !== null && (
                <div className="font-sans font-light text-sm leading-normal mb-4 text-foreground md:max-w-3xl inline-block md:col-start-1 md:row-start-2">
                    {subtitle}
                </div>
            )}

            {controls !== null && (
                <div className="flex items-center gap-2 shrink-0 mt-4 md:mt-0 md:col-start-2 md:row-start-1 md:self-center md:justify-self-end">
                    {controls}
                </div>
            )}
        </div>
    );
}
